import { joinDefined } from "@client/lib/misc";
import { getCompositeSettingV2, getInherittedSettingValueV2 } from "@client/lib/settings";
import {
  type EntityRef,
  type LocalSettings,
  type MainSettings,
  type SettingValue,
  type WithContent,
  type WithEntitiesSource,
  type WithId,
  type WithSettings,
  type WithStyleSheet
} from "@client/types";
import { isNull, isUndefined } from "lodash";
import { createSelector, type Selector } from "reselect";
import { createShallowEqualSelector } from "../common";
import { localSettingsSelector, makeEntitySettingsSelector } from "../entities";
import { layoutLocalSelector, layoutSettingsSelector } from "../layout";
import { idPropsSelector, prefixPropsSelector } from "../props";
import * as stateSelectors from "../state";
import {
  type OutputParametricSelector,
  type ParametricSelector,
  type SettingsParametricSelector,
  type SettingsPropsParametricSelector
} from "../types";
import { getColorSelector } from "./styleSheet";

const makeLocalSelector = () =>
  createSelector([localSettingsSelector, idPropsSelector], (local, id): LocalSettings => (local ? local[id] : null));

export const TargetSelector = {
  Entity: "Entity",
  Layout: "Layout"
};
const getLocalSelector = (
  target: string
): ParametricSelector<WithSettings | WithStyleSheet, void | Partial<WithId & WithEntitiesSource>, LocalSettings> => {
  switch (target) {
    case TargetSelector.Entity:
      return makeLocalSelector() as ParametricSelector<WithSettings & WithContent, void | EntityRef, LocalSettings>;
    case TargetSelector.Layout:
      return layoutLocalSelector as ParametricSelector<WithStyleSheet, void | EntityRef, LocalSettings>;
    default:
      throw new Error(`BUG. Unsupported target: ${target}`);
  }
};
const getSettingsSelector = (
  target: string
): ParametricSelector<WithSettings | WithStyleSheet, void | Partial<WithId & WithEntitiesSource>, MainSettings> => {
  switch (target) {
    case TargetSelector.Entity:
      return makeEntitySettingsSelector() as ParametricSelector<
        WithSettings & WithContent,
        void | EntityRef,
        MainSettings
      >;
    case TargetSelector.Layout:
      return layoutSettingsSelector as ParametricSelector<WithStyleSheet, void | EntityRef, MainSettings>;
    default:
      throw new Error(`BUG. Unsupported target: ${target}`);
  }
};

export const getSettingSelector = <T = any>(
  target: string,
  name: string,
  prefix?: string
): SettingsParametricSelector<T> | SettingsPropsParametricSelector<T> => {
  const settingsSelector = getSettingsSelector(target);
  const localSelector = getLocalSelector(target);
  return createSelector(
    [settingsSelector, localSelector, stateSelectors.screenSelector, prefixPropsSelector],
    (settings, local, screen, prefixProps) => {
      if (!settings) return; // when the target is Layout, there can be no settings

      return getInherittedSettingValueV2(settings, local, prefixProps ?? prefix, name, screen);
    }
  );
};

export const getCompositeSettingSelector = <T extends {}>(target: string, name: string, prefix?: string) => {
  // TODO: TS : SettingsParametricSelector<Partial<T> | null> | SettingsPropsParametricSelector<Partial<T> | null>
  const localSelector = getLocalSelector(target);
  const settingsSelector = getSettingsSelector(target);
  return createSelector(
    [settingsSelector, localSelector, stateSelectors.screenSelector, prefixPropsSelector],
    (settings, local, screen, prefixProps) => {
      if (!settings && !local) return; // when the target is Layout, there can be no settings

      return getCompositeSettingV2<T>(settings, local, prefixProps ?? prefix, name, screen);
    }
  );
};

export const getLayoutCompositeSettingSelector = <T extends {}>(name: string, prefix?: string) =>
  getCompositeSettingSelector<T>(TargetSelector.Layout, name, prefix);

export const getSettingValueSelector = <T = any>(
  target: string,
  name: string,
  prefix?: string,
  converter?: (value: any) => any
): SettingsParametricSelector<T> | SettingsPropsParametricSelector<T> => {
  const settingSelector = getSettingSelector(target, name, prefix);
  return converter ? createSelector([settingSelector], converter) : settingSelector;
};
export const getLayoutSettingValueSelector = (
  name: string,
  prefix?: string,
  converter?: (value: any) => any
): Selector<any, any, any> => getSettingValueSelector(TargetSelector.Layout, name, prefix, converter);

export const getLayoutColorSelector = (name: string, prefix?: string): Selector<any, any, any> =>
  getColorSelector(TargetSelector.Layout, name, prefix);

export const getSettingValueSelectorWithDefault = (
  target: string,
  name: string,
  defaultValueSelector: OutputParametricSelector<any, any, any, any>,
  prefix?: string
) => {
  const settingValueSelector = getSettingValueSelector(target, name, prefix);

  return createSelector([settingValueSelector, defaultValueSelector], (value, defaultValue) =>
    isNull(value) || isUndefined(value) ? defaultValue : value
  );
};

export type CombinedSettingsSelectorSetting<T> = {
  name: string;
  prop: Extract<keyof T, string>;
  converter?: (value: any) => any;
  path?: string;
  type?: "color";
};
export const combinedSettingsSelector = <T extends {} = any>(
  target: string,
  settings: Array<Extract<keyof T, string> | CombinedSettingsSelectorSetting<T>>,
  prefix?: string
): SettingsParametricSelector<T> => {
  const convertedSettings = settings.map<CombinedSettingsSelectorSetting<T>>((x) =>
    typeof x === "string" ? { name: x, prop: x } : x
  );

  return createShallowEqualSelector(
    convertedSettings.map((s) =>
      s.type === "color"
        ? getColorSelector(target, s.name, joinDefined(".", prefix, s.path))
        : getSettingValueSelector(target, s.name, joinDefined(".", prefix, s.path))
    ),
    (...values): T =>
      values.reduce((res: object, value: SettingValue, index: number) => {
        const setting = convertedSettings[index];
        const name = setting.prop ?? setting.name;
        return { ...res, [name]: setting.converter ? setting.converter(value) : value };
      }, {})
  );
};
